import React, { useEffect, useState } from 'react';
import Button from '../../shared/components/ui/Button';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  getAllBlocks,
  getUnitPricingByBlockId,
  updateUnitStatus,
  getUnitStatus,
  bulkBookUnits,
  getAllActiveSalesUsersByProjectId
} from '../services/api/implementation/impl';
import { useParams } from 'react-router-dom';
import Table from '../../shared/components/layout/Table';
import { useForm } from 'react-hook-form';
import { transformData } from '../../super-admin/utils/transformData';
import toast from 'react-hot-toast';
import { UpdateStatus } from '../components/forms/UpdateStatus';
import Modal from '../../shared/components/ui/Modal';
import PaginationComponent from '../../shared/components/PaginationComponent';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import { createPortal } from 'react-dom';
import { ChevronDown, Info } from 'lucide-react';

/**
 * Define all possible transitions in a single map:
 * currentStatus => array of allowed next statuses
 */
const statusTransitions = {
  Vacant: ['Blocked', 'Blocked_for_Broker', 'Booked'],

  // If current status is "Blocked", it can transition to "Vacant" or "Blocked_for_Broker".
  Blocked: ['Vacant', 'Blocked_for_Broker'],

  // If current status is "Blocked_for_Broker", it can go to "Vacant" or "Blocked".
  Blocked_for_Broker: ['Vacant', 'Blocked'],

  // If current status is "Booked", it can transition to the following four statuses.
  Booked: [
    'Temporary_Booked',
    'Sales_Deed_Completed',
    'Sales_Deed_with_Pending_Payments',
    'No_Dues'
  ],

  // If current status is "Temporary_Booked", it can move to these four.
  Temporary_Booked: [
    'Sales_Deed_Completed',
    'Sales_Deed_with_Pending_Payments',
    'No_Dues',
    'Booked'
  ],

  // If current status is "Sales_Deed_Completed", it can move to these four.
  Sales_Deed_Completed: [
    'Temporary_Booked',
    'Sales_Deed_with_Pending_Payments',
    'No_Dues',
    'Booked'
  ],

  // If current status is "Sales_Deed_with_Pending_Payments", it can move to these four.
  Sales_Deed_with_Pending_Payments: [
    'Temporary_Booked',
    'Sales_Deed_Completed',
    'No_Dues',
    'Booked'
  ],

  // If current status is "No_Dues", it can move to these four.
  No_Dues: [
    'Temporary_Booked',
    'Sales_Deed_Completed',
    'Sales_Deed_with_Pending_Payments',
    'Booked'
  ]
};

const UnitPricingList = ({
  projectName,
  bookPortalEl = null,
  anchorBookInPricingSubtab = false
}) => {
  const userId = Cookies.get('userId');
  const { id: projectId } = useParams();
  const { register } = useForm();
  const [newBlockId, setNewBlockId] = useState('');
  const [blocksDynaWithLevels, setBlocksDynaWithLevels] = useState([]);
  const [openUpdateStatus, setOpenUpdateStatus] = useState(false);
  const [selectedUnitId, setSelectedUnitId] = useState(null);
  const [updateStatusOptions, setUpdateStatusOptions] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const { collapsed } = useSidebar();
  // Pagination removed
  const page = 1;
  const limit = 20000; // fetch more per page
  const [expandedRows, setExpandedRows] = useState([]);

  // Checkbox selection state
  const [selectedUnits, setSelectedUnits] = useState([]);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedSalesPerson, setSelectedSalesPerson] = useState('');
  const [selectedBookAs, setSelectedBookAs] = useState('');
  const [isBookingSubmitting, setIsBookingSubmitting] = useState(false);

  // Dropdown state for header portal dropdown
  const [openDropdown, setOpenDropdown] = useState(null);
  const blockHeaderRef = React.useRef(null);
  const [blockDropdownRect, setBlockDropdownRect] = useState(null);
  const statusHeaderRef = React.useRef(null);
  const [statusDropdownRect, setStatusDropdownRect] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState('Select Status');
  const [extraChargesBreakdownUnit, setExtraChargesBreakdownUnit] = useState(null);
  const [extraChargesBreakdownClosedDeal, setExtraChargesBreakdownClosedDeal] = useState(null);

  // === Queries ===
  const { data: blocksDyna } = useQuery({
    queryKey: ['blocks', projectId],
    queryFn: () => getAllBlocks(projectId),
    enabled: !!projectId
  });

  const {
    data: unitPricingData,
    refetch: refetchUnitPricingData,
    isFetching
  } = useQuery({
    queryKey: ['unitPricing', newBlockId, page, selectedStatus],
    queryFn: () => getUnitPricingByBlockId(newBlockId, page, limit, selectedStatus),
    keepPreviousData: true,
    enabled: !!newBlockId // Only fetch if we have a valid block ID
  });

  const { data: unitStatus } = useQuery({
    queryKey: ['unitStatus'],
    queryFn: () => getUnitStatus(),
    enabled: true
  });

  const { data: salesUsers } = useQuery({
    queryKey: ['salesUsers', projectId],
    queryFn: () => getAllActiveSalesUsersByProjectId(projectId),
    enabled: !!projectId
  });

  const handleUnitStatusUpdate = (data) => {
    const payload = { status: data?.status };
    handleUnitUpdate(payload);
  };

  // === Mutations ===
  const { mutate: handleUnitUpdate } = useMutation({
    mutationFn: (payload) => updateUnitStatus(selectedUnitId, payload),
    onMutate: () => {
      setIsSubmitting(true);
    },
    onSuccess: async () => {
      setIsSubmitting(false);
      toast.success('Unit status updated successfully');
      setOpenUpdateStatus(false);
      await refetchUnitPricingData();
    },
    onError: (err) => {
      setIsSubmitting(false);
      console.error('Error updating unit status', err);
      toast.error(err?.response?.data?.message || err?.message || 'Error updating unit status');
    }
  });

  // Bulk booking mutation
  const { mutate: handleBulkBooking } = useMutation({
    mutationFn: (payload) => bulkBookUnits(payload),
    onMutate: () => {
      setIsBookingSubmitting(true);
    },
    onSuccess: async () => {
      setIsBookingSubmitting(false);
      toast.success('Units booked successfully');
      setShowBookingModal(false);
      setSelectedUnits([]);
      setSelectedSalesPerson('');
      setSelectedBookAs('');
      await refetchUnitPricingData();
    },
    onError: (err) => {
      setIsBookingSubmitting(false);
      console.error('Error booking units', err);
      toast.error(err?.response?.data?.message || err?.message || 'Error booking units');
    }
  });

  // === Effects ===

  // Track page view
  useEffect(() => {
    mixpanel.track('Unit Pricing List Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    // Filter out only blocks that have levels, then set the first one as default
    if (Array.isArray(blocksDyna) && blocksDyna.length > 0) {
      const blocksWithLevels = blocksDyna.filter((block) => block?.levels?.length > 0);
      const sorted = blocksWithLevels.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      sorted.sort((a, b) => a.name.localeCompare(b.name));
      if (sorted.length > 0) {
        setBlocksDynaWithLevels(sorted);
        // If there's already a block selected, don't overwrite it;
        // otherwise default to the newest block
        setNewBlockId((prev) => (prev ? prev : sorted[0].id));
      }
    }
  }, [blocksDyna]);

  // Whenever block changes, refetch units
  useEffect(() => {
    if (newBlockId) {
      refetchUnitPricingData();
    }
  }, [newBlockId, refetchUnitPricingData]);

  // For debugging: log the statuses we fetched
  useEffect(() => {
    if (unitStatus) {
      console.log('Fetched unitStatus from API:', unitStatus);
    }
  }, [unitStatus]);

  // === Data transformations ===
  const UnitPricingFormat = [
    'id',
    'unitName',
    'cost',
    'plc_price',
    'gst',
    'extra_charges',
    'total_price',
    'final_total_price',
    'accommodationName',
    'status',
    'actions'
  ];

  const RequiredUnitPricingData =
    unitPricingData?.data && unitPricingData?.data.length > 0
      ? unitPricingData.data
          .map((item) =>
            transformData(
              {
                ...item,
                cost: parseInt(item?.cost) || 0,
                plc_price: Math.ceil(item?.plc_price) || 0,
                gst: `₹${(parseInt(item?.gst) || 0).toLocaleString('en-IN')}.00`,
                extra_charges: `₹${(parseInt(item.extra_charges) || 0).toLocaleString('en-IN')}.00`,
                total_price: `₹${(
                  (parseInt(item?.cost) || 0) +
                  (Math.ceil(item?.plc_price) || 0) +
                  (parseInt(item?.gst) || 0)
                ).toLocaleString('en-IN')}.00`,
                final_total_price: `₹${(
                  (parseInt(item?.cost) || 0) +
                  (Math.ceil(item?.plc_price) || 0) +
                  (parseInt(item?.gst) || 0) +
                  (parseInt(item?.extra_charges) || 0)
                ).toLocaleString('en-IN')}.00`,
                accommodationName: item?.accommodation?.name,
                status: item?.status
              },
              UnitPricingFormat
            )
          )
          // Sort by id in ascending order
          .sort((a, b) => a.id - b.id)
      : [];

  // === Handlers ===
  const handleSelectBlockChange = (event) => {
    const selectedBlockId = event.target.value;
    setNewBlockId(selectedBlockId);
  };

  /**
   * Dynamically filter possible status updates based on current unit status,
   * using the `statusTransitions` map above.
   */
  const handleStatusUpdate = (unitId) => {
    setOpenUpdateStatus(true);
    setSelectedUnitId(unitId);

    // Find the unit in the data
    const currentUnit = unitPricingData?.data?.find((u) => parseInt(u.id) === parseInt(unitId));
    setSelectedUnit(currentUnit);

    if (!currentUnit || !unitStatus) {
      setUpdateStatusOptions([]);
      return;
    }

    const currentStatus = currentUnit.status;
    const allowedNextStatuses = statusTransitions[currentStatus] || [];
    const newOptions = unitStatus.filter((s) => allowedNextStatuses.includes(s));
    setUpdateStatusOptions(newOptions);
  };

  // Toggle expand/collapse
  const handleToggleExpand = (unitId) => {
    setExpandedRows((prev) =>
      prev.includes(unitId) ? prev.filter((id) => id !== unitId) : [...prev, unitId]
    );
  };

  // Pagination
  // Pagination removed
  const totalPages = 1;
  const handlePageChange = () => {};

  // Current block name for display in table
  const currentBlockName =
    blocksDynaWithLevels?.find((b) => String(b.id) === String(newBlockId))?.name || '---';

  // PortalDropdown component
  const PortalDropdown = ({ anchorRect, children, onClose }) => {
    if (!anchorRect) return null;
    return createPortal(
      <div
        className="absolute bg-white border rounded shadow z-[1000]"
        style={{
          position: 'fixed',
          left: anchorRect.left,
          top: anchorRect.bottom,
          minWidth: anchorRect.width,
          maxHeight: '250px',
          overflowY: 'auto'
        }}
        onClick={(e) => e.stopPropagation()}>
        {children}
      </div>,
      document.body
    );
  };

  const handleHeaderDropdownClick = (type) => {
    // toggle dropdown
    const next = openDropdown === type ? null : type;
    setOpenDropdown(next);
    if (next === 'block' && blockHeaderRef?.current) {
      setBlockDropdownRect(blockHeaderRef.current.getBoundingClientRect());
    }
    if (next === 'status' && statusHeaderRef?.current) {
      setStatusDropdownRect(statusHeaderRef.current.getBoundingClientRect());
    }
  };

  const handleCloseDropdown = () => {
    setOpenDropdown(null);
    setBlockDropdownRect(null);
    setStatusDropdownRect(null);
  };

  const handleSelectDropdownItemFromHeader = (blockId) => {
    setNewBlockId(blockId);
    handleCloseDropdown();
  };

  const handleSelectStatusFromHeader = (status) => {
    setSelectedStatus(status);
    handleCloseDropdown();
  };

  // Handle unit checkbox selection
  const handleUnitSelection = (unitId, isChecked) => {
    if (isChecked) {
      setSelectedUnits((prev) => [...prev, unitId]);
    } else {
      setSelectedUnits((prev) => prev.filter((id) => id !== unitId));
    }
  };

  // Handle book button click
  const handleBookClick = () => {
    if (selectedUnits.length >= 2) {
      setShowBookingModal(true);
    }
  };

  // Handle booking submission
  const handleBookingSubmit = () => {
    if (!selectedBookAs) {
      toast.error('Please select "Book as" option');
      return;
    }

    if (!selectedSalesPerson) {
      toast.error('Please select a sales person');
      return;
    }

    // Determine status based on "Book as" selection
    const status =
      selectedBookAs === 'Close Deal' ? 'Booked_Incomplete' : 'Temporary_Booked_Incomplete';

    const payload = {
      status: status,
      ids: selectedUnits,
      sales_id: Number(selectedSalesPerson)
    };

    handleBulkBooking(payload);
  };

  return (
    <>
      {openUpdateStatus && (
        <Modal
          isDirty={true}
          confirmOnClose={true}
          width={40}
          name="Update Status"
          onClose={() => setOpenUpdateStatus(false)}>
          <UpdateStatus
            onClose={() => setOpenUpdateStatus(false)}
            statuses={updateStatusOptions}
            isSubmitting={isSubmitting}
            onSubmit={handleUnitStatusUpdate}
            type={'unitPricing'}
            unitData={selectedUnit}
            projectName={projectName}
            projectId={projectId}
          />
        </Modal>
      )}

      {showBookingModal && (
        <Modal
          isDirty={true}
          confirmOnClose={true}
          width={40}
          name="Book Units"
          onClose={() => {
            setShowBookingModal(false);
            setSelectedBookAs('');
            setSelectedSalesPerson('');
          }}>
          <div className="p-6">
            <div className="mb-4">
              <p className="text-sm text-gray-600">
                Please select booking type and sales person to assign these units to.
              </p>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Book as <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedBookAs}
                onChange={(e) => setSelectedBookAs(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500">
                <option value="">Select booking type</option>
                <option value="Under Consideration">Under Consideration</option>
                <option value="Close Deal">Close Deal</option>
              </select>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Sales Person <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedSalesPerson}
                onChange={(e) => setSelectedSalesPerson(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500">
                <option value="">Select a sales person</option>
                {salesUsers?.data?.map((sales) => (
                  <option key={sales.id} value={sales.id}>
                    {sales.name || sales.email}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex justify-end gap-3">
              <Button
                type="button"
                displayType="secondary"
                onClick={() => {
                  setShowBookingModal(false);
                  setSelectedBookAs('');
                  setSelectedSalesPerson('');
                }}
                disabled={isBookingSubmitting}>
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleBookingSubmit}
                disabled={isBookingSubmitting || !selectedBookAs || !selectedSalesPerson}>
                {isBookingSubmitting ? 'Booking...' : 'Book Units'}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {extraChargesBreakdownUnit && (
        <Modal
          width={50}
          name="Extra Charges Breakdown"
          onClose={() => setExtraChargesBreakdownUnit(null)}>
          <div className="p-4">
            <p className="text-sm text-gray-600 mb-3">
              Unit: <span className="font-medium text-gray-800">{extraChargesBreakdownUnit.unitName}</span>
              {extraChargesBreakdownUnit.accommodation?.name && (
                <> · {extraChargesBreakdownUnit.accommodation.name}</>
              )}
            </p>
            <p className="text-sm text-gray-600 mb-4">
              Total extra charges: ₹{(Number(extraChargesBreakdownUnit.extra_charges) || 0).toLocaleString('en-IN')}
            </p>
            {Array.isArray(extraChargesBreakdownUnit.extra_charges_arr) &&
            extraChargesBreakdownUnit.extra_charges_arr.length > 0 ? (
              <div className="overflow-x-auto rounded border border-gray-200">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-gray-700">Name</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-700">Mode</th>
                      <th className="px-3 py-2 text-right font-medium text-gray-700">Price</th>
                      <th className="px-3 py-2 text-right font-medium text-gray-700">GST (% / ₹)</th>
                      <th className="px-3 py-2 text-right font-medium text-gray-700">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {extraChargesBreakdownUnit.extra_charges_arr.map((charge, i) => {
                      const totalPrice = Number(charge.total_price) || 0;
                      const gstPct = Number(charge.gst) || 0;
                      const gstAmount = totalPrice * gstPct / 100;
                      const totalWithGst = totalPrice + gstAmount;
                      return (
                        <tr key={charge.id || i} className="border-t border-gray-100 hover:bg-gray-50">
                          <td className="px-3 py-2 text-gray-800">{charge.name || '---'}</td>
                          <td className="px-3 py-2 text-gray-600">{charge.mode || '---'}</td>
                          <td className="px-3 py-2 text-right">
                            ₹{(Number(charge.price) || 0).toLocaleString('en-IN')}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {charge.gst != null && charge.gst !== '' ? (
                              <>
                                {Number(charge.gst)}% (₹{gstAmount.toLocaleString('en-IN')})
                              </>
                            ) : (
                              '---'
                            )}
                          </td>
                          <td className="px-3 py-2 text-right font-medium">
                            ₹{totalWithGst.toLocaleString('en-IN')}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-gray-500">No breakdown available for this unit.</p>
            )}
          </div>
        </Modal>
      )}

      {extraChargesBreakdownClosedDeal && (
        <Modal
          width={50}
          name="Extra Charges Breakdown (Deal Closed)"
          onClose={() => setExtraChargesBreakdownClosedDeal(null)}>
          <div className="p-4">
            <p className="text-sm text-gray-600 mb-3">
              Unit: <span className="font-medium text-gray-800">{extraChargesBreakdownClosedDeal.unitName || '---'}</span>
              {extraChargesBreakdownClosedDeal.accommodationName && (
                <> · {extraChargesBreakdownClosedDeal.accommodationName}</>
              )}
            </p>
            <p className="text-sm text-gray-600 mb-4">
              Total extra charges: ₹{(Number(extraChargesBreakdownClosedDeal.closedDealItem?.extra_charges) || 0).toLocaleString('en-IN')}
            </p>
            {Array.isArray(extraChargesBreakdownClosedDeal.closedDealItem?.extra_charges_snapshot) &&
            extraChargesBreakdownClosedDeal.closedDealItem.extra_charges_snapshot.length > 0 ? (
              <div className="overflow-x-auto rounded border border-gray-200">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-gray-700">Name</th>
                      <th className="px-3 py-2 text-right font-medium text-gray-700">Price</th>
                      <th className="px-3 py-2 text-right font-medium text-gray-700">GST (% / ₹)</th>
                      <th className="px-3 py-2 text-right font-medium text-gray-700">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {extraChargesBreakdownClosedDeal.closedDealItem.extra_charges_snapshot.map((charge, i) => {
                      const totalPrice = Number(charge.total_price) ?? Number(charge.price) ?? 0;
                      const gstPct = Number(charge.gst) || 0;
                      const gstAmount = totalPrice * gstPct / 100;
                      const totalWithGst = totalPrice + gstAmount;
                      return (
                        <tr key={charge.id || i} className="border-t border-gray-100 hover:bg-gray-50">
                          <td className="px-3 py-2 text-gray-800">{charge.name || '---'}</td>
                          <td className="px-3 py-2 text-right">
                            ₹{((Number(charge.price) ?? totalPrice) || 0).toLocaleString('en-IN')}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {charge.gst != null && charge.gst !== '' ? (
                              <>
                                {Number(charge.gst)}% (₹{gstAmount.toLocaleString('en-IN')})
                              </>
                            ) : (
                              '---'
                            )}
                          </td>
                          <td className="px-3 py-2 text-right font-medium">
                            ₹{totalWithGst.toLocaleString('en-IN')}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-gray-500">No breakdown available for this deal.</p>
            )}
          </div>
        </Modal>
      )}

      {anchorBookInPricingSubtab &&
        bookPortalEl &&
        createPortal(
          <Button
            type="button"
            onClick={handleBookClick}
            disabled={selectedUnits.length < 2}
            customStyle={{
              padding: '0.75rem 1.5rem',
              fontSize: '14px',
              opacity: selectedUnits.length < 2 ? 0.5 : 1,
              cursor: selectedUnits.length < 2 ? 'not-allowed' : 'pointer'
            }}>
            Book ({selectedUnits.length} selected)
          </Button>,
          bookPortalEl
        )}

      <div
        className={`transition-all duration-300 mt-3 ease-in-out w-full flex flex-col px-3 sm:px-4 md:px-5 bg-gray-100 overflow-hidden`}>
        {!anchorBookInPricingSubtab && (
          <div className="flex justify-end">
            <Button
              type="button"
              onClick={handleBookClick}
              disabled={selectedUnits.length < 2}
              customStyle={{
                padding: '0.75rem 1.5rem',
                fontSize: '14px',
                opacity: selectedUnits.length < 2 ? 0.5 : 1,
                cursor: selectedUnits.length < 2 ? 'not-allowed' : 'pointer'
              }}>
              Book ({selectedUnits.length} selected)
            </Button>
          </div>
        )}

        {/* Responsive table wrapper: horizontal + vertical scroll */}
        <div
          className={`unit-pricing-table-container flex flex-col min-h-0 flex-1 rounded-xl border border-gray-200 bg-white shadow-sm ${
            anchorBookInPricingSubtab ? 'mt-3' : 'mt-5'
          }`}>
          <style>{`
            .unit-pricing-table-container {
              max-height: min(calc(100vh - 11rem), 800px);
              overflow: auto;
              -webkit-overflow-scrolling: touch;
              scrollbar-width: thin;
            }
            .unit-pricing-table-container::-webkit-scrollbar {
              height: 8px;
              width: 8px;
            }
            .unit-pricing-table-container::-webkit-scrollbar-track {
              background: #f1f5f9;
              border-radius: 4px;
            }
            .unit-pricing-table-container::-webkit-scrollbar-thumb {
              background: #cbd5e1;
              border-radius: 4px;
            }
            .unit-pricing-table-container::-webkit-scrollbar-thumb:hover {
              background: #94a3b8;
            }
          `}</style>
          <div className="w-full relative min-w-0">
            <table className="unit-pricing-table text-left text-sm w-full min-w-[1100px] border-collapse">
              <thead className="bg-gray-100 text-gray-600 w-full sticky top-0 z-20">
                <tr className="h-12 w-full">
                  <th className="px-2 sm:px-4 py-2 border-b border-gray-200 font-medium w-12 sm:w-14 shrink-0 rounded-tl-xl">Select</th>
                  <th className="px-2 sm:px-4 py-2 border-b border-gray-200 font-medium relative min-w-[5rem] shrink-0" ref={blockHeaderRef}>
                    <span>Block Name</span>
                    <button
                      className="ml-1 align-middle"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleHeaderDropdownClick('block');
                      }}>
                      <ChevronDown size={16} />
                    </button>
                    {openDropdown === 'block' && blockDropdownRect && (
                      <PortalDropdown anchorRect={blockDropdownRect} onClose={handleCloseDropdown}>
                        {blocksDynaWithLevels?.map((block) => (
                          <div
                            key={block.id}
                            className={`px-4 py-2 hover:bg-gray-100 cursor-pointer ${
                              String(block.id) === String(newBlockId) ? 'bg-blue-100' : ''
                            }`}
                            onClick={() => handleSelectDropdownItemFromHeader(block.id)}>
                            {block.name}
                          </div>
                        ))}
                      </PortalDropdown>
                    )}
                  </th>
                  <th className="px-2 sm:px-4 py-2 border-b border-gray-200 font-medium sticky left-[8.5rem] z-10 bg-gray-100 min-w-[6rem] shadow-[2px_0_6px_-2px_rgba(0,0,0,0.08)]">
                    Unit Name
                  </th>
                  <th className="px-2 sm:px-4 py-2 border-b border-gray-200 font-medium whitespace-nowrap">Unit Price</th>
                  <th className="px-2 sm:px-4 py-2 border-b border-gray-200 font-medium whitespace-nowrap">Total Plc Price</th>
                  {/* <th className="px-2 sm:px-4 py-2 border-b font-normal whitespace-nowrap">GST</th> */}
                  {/* <th className="px-2 sm:px-4 py-2 border-b font-normal whitespace-nowrap">Total Price</th> */}
                  <th className="px-2 sm:px-4 py-2 border-b border-gray-200 font-medium whitespace-nowrap">Extra Charges</th>
                  <th className="px-2 sm:px-4 py-2 border-b border-gray-200 font-medium whitespace-nowrap">Final Total Price</th>
                  <th className="px-2 sm:px-4 py-2 border-b border-gray-200 font-medium whitespace-nowrap">Discount</th>
                  <th className="px-2 sm:px-4 py-2 border-b border-gray-200 font-medium whitespace-nowrap">Due Price</th>
                  <th className="px-2 sm:px-4 py-2 border-b border-gray-200 font-medium min-w-[4rem]">Acc Name</th>
                  <th className="px-2 sm:px-4 py-2 border-b border-gray-200 font-medium relative min-w-[5rem]" ref={statusHeaderRef}>
                    <span>Status</span>
                    <button
                      className="ml-1 align-middle"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleHeaderDropdownClick('status');
                      }}>
                      <ChevronDown size={16} />
                    </button>
                    {openDropdown === 'status' && statusDropdownRect && (
                      <PortalDropdown anchorRect={statusDropdownRect} onClose={handleCloseDropdown}>
                        <div
                          className={`px-4 py-2 hover:bg-gray-100 cursor-pointer ${
                            selectedStatus === 'Select Status' ? 'bg-blue-100' : ''
                          }`}
                          onClick={() => handleSelectStatusFromHeader('Select Status')}>
                          Select Status
                        </div>
                        {unitStatus?.map((s) => (
                          <div
                            key={s}
                            className={`px-4 py-2 hover:bg-gray-100 cursor-pointer ${
                              selectedStatus === s ? 'bg-blue-100' : ''
                            }`}
                            onClick={() => handleSelectStatusFromHeader(s)}>
                            {s}
                          </div>
                        ))}
                      </PortalDropdown>
                    )}
                  </th>
                  <th className="px-2 sm:px-4 py-2 border-b border-gray-200 font-medium whitespace-nowrap rounded-tr-xl">Action</th>
                </tr>
              </thead>
              <tbody>
                {RequiredUnitPricingData && RequiredUnitPricingData.length > 0 ? (
                  RequiredUnitPricingData.map((row, idx) => {
                    // Find the original data for this row (to get underConsideration/closedDeal/pricingHistory)
                    const original = unitPricingData?.data?.find((u) => u.id === row.id);
                    const hasExpandable =
                      original?.underConsideration?.length > 0 ||
                      original?.closedDeal?.length > 0 ||
                      original?.pricingHistory?.length > 1;
                    return (
                      <React.Fragment key={row.id || idx}>
                        <tr className="border-b border-gray-100 hover:bg-gray-50 bg-white h-[3.35rem]">
                          <td className="px-2 sm:px-4 py-2 align-middle w-12 sm:w-14 shrink-0">
                            {row.status === 'Vacant' && (
                              <input
                                type="checkbox"
                                checked={selectedUnits.includes(row.id)}
                                onChange={(e) => handleUnitSelection(row.id, e.target.checked)}
                                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded "
                              />
                            )}
                          </td>
                          <td className="px-2 sm:px-4 py-2 align-middle min-w-[5rem] shrink-0">{idx === 0 ? currentBlockName : ''}</td>
                          <td className="px-2 sm:px-4 py-2 align-middle sticky left-[8.5rem] z-[5] bg-white hover:bg-gray-50 min-w-[6rem] shadow-[2px_0_6px_-2px_rgba(0,0,0,0.06)]">
                            {hasExpandable && (
                              <button
                                onClick={() => handleToggleExpand(row.id)}
                                style={{ marginRight: 8 }}>
                                {expandedRows.includes(row.id) ? '-' : '+'}
                              </button>
                            )}
                            {row.unitName || '---'}
                          </td>
                          <td className="px-2 sm:px-4 py-2 align-middle whitespace-nowrap">{row.cost || '---'}</td>
                          <td className="px-2 sm:px-4 py-2 align-middle whitespace-nowrap">{row.plc_price || '---'}</td>
                          {/* <td className="px-2 sm:px-4 py-2 align-middle whitespace-nowrap">{row.gst || '---'}</td> */}
                          {/* <td className="px-2 sm:px-4 py-2 align-middle whitespace-nowrap">{row.total_price || '---'}</td> */}
                          <td className="px-2 sm:px-4 py-2 align-middle whitespace-nowrap">
                            <span className="inline-flex items-center gap-1">
                              {row.extra_charges || '---'}
                              {(Number(original?.extra_charges) || 0) > 0 && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setExtraChargesBreakdownUnit(original);
                                  }}
                                  className="p-0.5 rounded hover:bg-gray-200 text-gray-500 hover:text-blue-600 transition-colors"
                                  title="View extra charges breakdown">
                                  <Info size={16} />
                                </button>
                              )}
                            </span>
                          </td>
                          <td className="px-2 sm:px-4 py-2 align-middle whitespace-nowrap">{row.final_total_price || '---'}</td>
                          <td className="px-2 sm:px-4 py-2 align-middle whitespace-nowrap">---</td>
                          <td className="px-2 sm:px-4 py-2 align-middle whitespace-nowrap">---</td>
                          <td className="px-2 sm:px-4 py-2 align-middle min-w-[4rem]">{row.accommodationName || '---'}</td>
                          <td className="px-2 sm:px-4 py-2 align-middle">{row.status || '---'}</td>
                          <td className="px-2 sm:px-4 py-2 align-middle whitespace-nowrap">
                            <Button
                              type="button"
                              customStyle={{
                                padding: '0.5rem 1rem',
                                fontSize: '13px',
                                display: 'flex',
                                gap: '5px',
                                alignItems: 'center'
                              }}
                              displayType={'downloadButton'}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleStatusUpdate(row.id);
                              }}>
                              Status
                            </Button>
                          </td>
                        </tr>
                        {/* Pricing History Sub-rows */}
                        {expandedRows.includes(row.id) &&
                          original?.pricingHistory?.length > 1 &&
                          original.pricingHistory.slice(1)?.map((ph, i) => {
                            // Calculate total price from cost + plc_price + gst
                            const cost = Number(ph.cost) || 0;
                            const plcPrice = Number(ph.plc_price) || 0;
                            const gst = Number(ph.gst) || 0;
                            const totalPrice = cost + plcPrice + gst;

                            return (
                              <tr key={ph.id || `ph-${i}`} className="bg-amber-50 mb-2">
                                <td className="px-4 py-2"></td>
                                <td className="px-4 py-2 font-semibold text-amber-800">
                                  Old Price v{i + 1}
                                </td>
                                <td className="px-4 py-2">---</td>
                                <td className="px-4 py-2">
                                  {cost > 0 ? `₹${cost.toLocaleString('en-IN')}` : '---'}
                                </td>
                                <td className="px-4 py-2">
                                  {plcPrice > 0 ? `₹${plcPrice.toLocaleString('en-IN')}` : '---'}
                                </td>
                                {/* <td className="px-4 py-2">
                                  {gst > 0 ? `₹${gst.toLocaleString('en-IN')}` : '---'}
                                </td> */}
                                {/* <td className="px-4 py-2">
                                  {totalPrice > 0
                                    ? `₹${totalPrice.toLocaleString('en-IN')}`
                                    : '---'}
                                </td> */}
                                <td className="px-4 py-2">
                                  {ph.extra_charges
                                    ? `₹${Number(ph.extra_charges).toLocaleString('en-IN')}`
                                    : '---'}
                                </td>
                                <td className="px-4 py-2">---</td>
                                <td className="px-4 py-2">---</td>
                                <td className="px-4 py-2">---</td>
                                <td className="px-4 py-2">---</td>
                                <td className="px-4 py-2">---</td>
                                <td className="px-4 py-2">---</td>
                              </tr>
                            );
                          })}
                        {/* Under Consideration Sub-row */}
                        {expandedRows.includes(row.id) &&
                          original?.underConsideration?.length > 0 &&
                          original.underConsideration.map((uc, i) => (
                            <tr key={uc.id || i} className="bg-blue-50 mb-2">
                              <td className="px-4 py-2"></td>
                              <td className="px-4 py-2 font-semibold text-blue-800">
                                Under Consideration
                              </td>
                              <td className="px-4 py-2">---</td>
                              <td className="px-4 py-2">
                                {uc.unit_price || uc.customer_proposal
                                  ? `₹${Number(uc.unit_price || uc.customer_proposal).toLocaleString('en-IN')}`
                                  : '---'}
                              </td>
                              <td className="px-4 py-2">
                                {uc.plc_price
                                  ? `₹${Number(uc.plc_price).toLocaleString('en-IN')}`
                                  : '---'}
                              </td>
                              {/* <td className="px-4 py-2">
                                {uc.gst_price || uc.gst
                                  ? `₹${Number(uc.gst_price || uc.gst).toLocaleString('en-IN')}`
                                  : '---'}
                              </td> */}
                              {/* <td className="px-4 py-2">
                                {uc.total_price
                                  ? `₹${Number(uc.total_price).toLocaleString('en-IN')}`
                                  : '---'}
                              </td> */}
                              <td className="px-4 py-2">
                                {uc.extra_charges
                                  ? `₹${Number(uc.extra_charges).toLocaleString('en-IN')}`
                                  : '---'}
                              </td>
                              <td className="px-4 py-2">---</td>
                              <td className="px-4 py-2">
                                {uc.discount
                                  ? `₹${Number(uc.discount).toLocaleString('en-IN')}`
                                  : '---'}
                              </td>
                              <td className="px-4 py-2">---</td>
                              <td className="px-4 py-2">---</td>
                              <td className="px-4 py-2">
                                {uc.approved ? 'Approved' : 'Approval' || '---'}
                              </td>
                              <td className="px-4 py-2">---</td>
                            </tr>
                          ))}
                        {/* Deal Closed Sub-row */}
                        {expandedRows.includes(row.id) &&
                          original?.closedDeal?.length > 0 &&
                          original.closedDeal?.map((cd, i) => (
                            <tr key={cd.id || i} className="bg-green-50 mb-2">
                              <td className="px-4 py-2"></td>
                              <td className="px-4 py-2 font-semibold text-green-800">
                                Deal Closed
                              </td>
                              <td className="px-4 py-2">---</td>
                              <td className="px-4 py-2">
                                {cd.unit_price
                                  ? `₹${Number(cd.unit_price).toLocaleString('en-IN')}`
                                  : '---'}
                              </td>
                              <td className="px-4 py-2">
                                {cd.plc_price
                                  ? `₹${Number(cd.plc_price).toLocaleString('en-IN')}`
                                  : '---'}
                              </td>
                              {/* <td className="px-4 py-2">
                                {cd.gst_price
                                  ? `₹${Number(cd.gst_price).toLocaleString('en-IN')}`
                                  : '---'}
                              </td> */}
                              {/* <td className="px-4 py-2">
                                {cd.total_price
                                  ? `₹${Number(cd.total_price).toLocaleString('en-IN')}`
                                  : '---'}
                              </td> */}
                              <td className="px-4 py-2">
                                <span className="inline-flex items-center gap-1">
                                  {cd.extra_charges
                                    ? `₹${Number(cd.extra_charges).toLocaleString('en-IN')}`
                                    : '---'}
                                  {(Number(cd.extra_charges) || 0) > 0 &&
                                    Array.isArray(cd.extra_charges_snapshot) &&
                                    cd.extra_charges_snapshot.length > 0 && (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setExtraChargesBreakdownClosedDeal({
                                          unitName: original?.unitName,
                                          accommodationName: original?.accommodation?.name,
                                          closedDealItem: cd
                                        });
                                      }}
                                      className="p-0.5 rounded hover:bg-gray-200 text-gray-500 hover:text-blue-600 transition-colors"
                                      title="View extra charges breakdown">
                                      <Info size={16} />
                                    </button>
                                  )}
                                </span>
                              </td>
                              <td className="px-4 py-2">---</td>
                              <td className="px-4 py-2">
                                {cd.discount
                                  ? `₹${Number(cd.discount).toLocaleString('en-IN')}`
                                  : '---'}
                              </td>
                              <td className="px-4 py-2">
                                {cd.due_price
                                  ? `₹${Number(cd.due_price).toLocaleString('en-IN')}`
                                  : '---'}
                              </td>
                              <td className="px-4 py-2">---</td>
                              <td className="px-4 py-2">{cd.status || 'N/A'}</td>
                              <td className="px-4 py-2">---</td>
                            </tr>
                          ))}
                      </React.Fragment>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={12} className="text-center py-10 align-middle">
                      <div className="flex flex-col items-center justify-center w-full h-full">
                        <h2 className="text-lg font-bold text-gray-800 mb-1">No Data Found</h2>
                        <p className="text-gray-600 text-sm">
                          The page you are trying to access has no data.
                          <br />
                          Proceed with adding data.
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
};

export default UnitPricingList;
